import { useCallback } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import {
  PRIVILEGE_ACTIONS,
  REQUEST_PRIVILEGES,
} from '../../../constants/privilege.constant';

/**
 * Thin permission-checking wrapper over AuthContext.
 *
 * Reads user/privileges from AuthContext (single fetch, shared state).
 * Provides convenience methods for RBAC checks.
 *
 * IMPORTANT: This hook does NOT make any API calls.
 * All data comes from AuthContext which fetches once and shares globally.
 */
const usePermissions = () => {
  const {
    user,
    allPrivileges,
    isSuper,
    isExternal,
    isLoading,
    refreshPermissions,
  } = useAuth();

  /**
   * Check if user has a specific privilege on a specific resource.
   * @param {string} resourceName - The resource to check access for
   * @param {string} privilegeName - The privilege action to check (default: READ)
   * @param {Object} options - Additional options
   * @param {boolean} options.excludeSuper - Excludes super admin from access (default: false)
   * @param {boolean} options.requireExternal - Requires external user (default: false)
   */
  const hasPrivilege = useCallback(
    (resourceName, privilegeName = PRIVILEGE_ACTIONS.READ, options = {}) => {
      const { excludeSuper = false, requireExternal = false } = options;

      // Super admin exclusion (for sensitive resources like requests)
      if (excludeSuper && isSuper) return false;

      // External user requirement (for customer-only features)
      if (requireExternal) return isExternal;

      // Super admin automatic access (default behavior for most resources)
      if (isSuper) return true;

      // Regular privilege checking for non-super users
      if (!allPrivileges.length) return false;

      return allPrivileges.some(
        (item) =>
          item.Privilege &&
          item.Privilege.name === privilegeName &&
          item.Privilege.Resource &&
          item.Privilege.Resource.name === resourceName
      );
    },
    [allPrivileges, isSuper, isExternal]
  );

  const hasReadPrivilegeOnAny = useCallback(
    (resourceNames, options = {}) => {
      if (!Array.isArray(resourceNames) || !resourceNames.length) return false;
      return resourceNames.some((name) =>
        hasPrivilege(name, PRIVILEGE_ACTIONS.READ, options)
      );
    },
    [hasPrivilege]
  );

  const hasReviewPrivilegeOnAny = useCallback(
    (resourceNames, options = {}) => {
      if (!Array.isArray(resourceNames) || !resourceNames.length) return false;
      return resourceNames.some((name) =>
        hasPrivilege(name, REQUEST_PRIVILEGES.REVIEW, options)
      );
    },
    [hasPrivilege]
  );

  return {
    userContext: {
      user,
      allPrivileges,
      isSuper,
      isExternal,
      isInternal: !isExternal,
      isProfileLoaded: !isLoading,
    },
    hasPrivilege,
    hasReadPrivilegeOnAny,
    hasReviewPrivilegeOnAny,
    isLoading,
    refreshPermissions,
  };
};

export default usePermissions;
